<template>
  <div class="container-fluid admin-coupons">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h4 class="font-weight-bold mb-0">쿠폰 발급</h4>
      <button class="btn btn-outline-primary btn-sm" @click="loadAll">새로고침</button>
    </div>
    <div v-if="message" class="alert" :class="messageOk ? 'alert-success' : 'alert-danger'">{{ message }}</div>

    <!-- 발급 -->
    <div class="card">
      <div class="card-header py-2"><h6 class="mb-0">발급 — 이메일 목록 · batch · 일수</h6></div>
      <div class="card-body">
        <p class="small text-muted mb-2">가입 전 이메일도 미리 지급됩니다 — 그 이메일로 가입하면 로그인 때 «이용권 사용하기»가 뜹니다. 대소문자·공백·gmail 점/+ 차이는 같은 사람으로 봅니다. 같은 batch·이미 권한 있음·관리자·구독 중은 건너뛰고 까닭을 남깁니다.</p>
        <textarea v-model="issueForm.emails" class="form-control mb-2" rows="4" placeholder="이메일 — 줄바꿈·쉼표로 여러 개"></textarea>
        <div class="d-flex flex-wrap align-items-center">
          <input v-model="issueForm.batch" class="form-control form-control-sm mr-2 mb-2" style="width: 220px;" placeholder="batch (예: 탈환의깃발1기)" />
          <input v-model.number="issueForm.days" type="number" min="1" class="form-control form-control-sm mr-2 mb-2" style="width: 100px;" placeholder="일수" />
          <span class="small mr-3 mb-2">일</span>
          <button class="btn btn-outline-secondary btn-sm mr-2 mb-2" :disabled="busy" @click="issue(true)">미리보기</button>
          <button class="btn btn-primary btn-sm mb-2" :disabled="busy" @click="issue(false)">발급</button>
        </div>
        <table v-if="issueResults.length" class="table table-sm table-bordered mt-2 mb-0">
          <thead><tr><th>이메일</th><th>가입</th><th>결과</th><th>까닭</th></tr></thead>
          <tbody>
            <tr v-for="r in issueResults" :key="r.email">
              <td>{{ r.email }}<span v-if="r.userEmail && r.userEmail.toLowerCase() !== r.email.toLowerCase()" class="text-muted small"> → {{ r.userEmail }}</span></td>
              <td>{{ r.registered ? '가입' : '미가입' }}</td>
              <td><span class="badge" :class="resultClass(r.result)">{{ resultLabel(r) }}</span></td>
              <td class="small">{{ reasonLabel(r.reason) }}<span v-if="r.warning" class="text-danger"> {{ r.warning }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 계정 이전 -->
    <div class="card">
      <div class="card-header py-2"><h6 class="mb-0">계정 이전 — A 이메일의 안 쓴 쿠폰을 B 이메일로</h6></div>
      <div class="card-body">
        <div class="d-flex flex-wrap align-items-center">
          <input v-model="transferForm.from" class="form-control form-control-sm mr-2 mb-2" style="width: 240px;" placeholder="A (지금 쿠폰 있는 이메일)" />
          <span class="mr-2 mb-2">→</span>
          <input v-model="transferForm.to" class="form-control form-control-sm mr-2 mb-2" style="width: 240px;" placeholder="B (옮길 이메일)" />
          <input v-model="transferForm.reason" class="form-control form-control-sm mr-2 mb-2" style="width: 200px;" placeholder="까닭(선택)" />
          <button class="btn btn-primary btn-sm mb-2" :disabled="busy" @click="transfer">이전</button>
        </div>
        <p class="small text-muted mb-0">이미 쓴 쿠폰은 옮기지 않습니다. 되돌리기는 아래 «기록»에서.</p>
      </div>
    </div>

    <!-- 쿠폰 목록 -->
    <div class="card">
      <div class="card-header py-2 d-flex align-items-center">
        <h6 class="mb-0 mr-3">쿠폰 목록</h6>
        <input v-model="listForm.q" class="form-control form-control-sm mr-2" style="width: 220px;" placeholder="이메일 검색" @keyup.enter="loadList" />
        <input v-model="listForm.batch" class="form-control form-control-sm mr-2" style="width: 160px;" placeholder="batch" @keyup.enter="loadList" />
        <button class="btn btn-sm btn-success" @click="loadList">조회</button>
      </div>
      <div class="card-body table-wrap">
        <table class="table table-sm table-bordered mb-0">
          <thead><tr><th>이메일</th><th>batch</th><th>일수</th><th>발급</th><th>상태</th><th></th></tr></thead>
          <tbody>
            <tr v-for="c in coupons" :key="c._id">
              <td>{{ c.email }}</td>
              <td>{{ c.batch }}</td>
              <td>{{ c.days }}</td>
              <td class="small">{{ fmt(c.issuedAt) }}<span v-if="c.issuedBy" class="text-muted"> · {{ c.issuedBy }}</span></td>
              <td class="small">
                <span v-if="c.usedAt" class="badge badge-success">사용 {{ fmt(c.usedAt) }}</span>
                <span v-else-if="c.revokedAt" class="badge badge-secondary">회수 {{ fmt(c.revokedAt) }}</span>
                <span v-else class="badge badge-info">안 씀</span>
                <span v-if="c.transfers && c.transfers.length" class="text-muted"> · 이전 {{ c.transfers.length }}회</span>
              </td>
              <td><button v-if="!c.usedAt && !c.revokedAt" class="btn btn-outline-danger btn-sm py-0" :disabled="busy" @click="revoke(c)">회수</button></td>
            </tr>
            <tr v-if="!coupons.length"><td colspan="6" class="text-center text-muted">쿠폰 없음</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 기록 -->
    <div class="card">
      <div class="card-header py-2"><h6 class="mb-0">기록 (최근 100) — 누가·언제·무엇</h6></div>
      <div class="card-body table-wrap">
        <table class="table table-sm table-bordered mb-0">
          <thead><tr><th>시각</th><th>누가</th><th>무엇</th><th>내용</th><th></th></tr></thead>
          <tbody>
            <tr v-for="l in logs" :key="l._id">
              <td class="small">{{ fmt(l.at) }}</td>
              <td class="small">{{ l.by }}</td>
              <td>{{ actionLabel(l.action) }}</td>
              <td class="small">{{ logText(l) }}</td>
              <td>
                <span v-if="l.undoneAt" class="small text-muted">되돌림 {{ fmt(l.undoneAt) }}</span>
                <button v-else-if="['issue','transfer','revoke'].includes(l.action)" class="btn btn-outline-secondary btn-sm py-0" :disabled="busy" @click="undo(l)">되돌리기</button>
              </td>
            </tr>
            <tr v-if="!logs.length"><td colspan="5" class="text-center text-muted">기록 없음</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 시트 자동 지급 -->
    <div class="card">
      <div class="card-header py-2"><h6 class="mb-0">구글시트 새 행 자동 지급</h6></div>
      <div class="card-body">
        <p class="small text-muted mb-2">시트의 «CSV 내보내기» 주소를 넣습니다. 켜 두면 운영 서버가 정한 시간마다(기본 4시간) 새 행만 확인해 지급합니다. 시트에 결과를 직접 쓰지는 못합니다 — 결과는 아래 표와 CSV 로 받습니다.</p>
        <div class="d-flex flex-wrap align-items-center">
          <input v-model="sheet.url" class="form-control form-control-sm mr-2 mb-2" style="width: 420px;" placeholder="https://docs.google.com/spreadsheets/d/…/export?format=csv&gid=…" />
          <input v-model="sheet.batch" class="form-control form-control-sm mr-2 mb-2" style="width: 160px;" placeholder="batch" />
          <input v-model.number="sheet.days" type="number" min="1" class="form-control form-control-sm mr-1 mb-2" style="width: 80px;" />
          <span class="small mr-2 mb-2">일</span>
          <input v-model.number="sheet.intervalHours" type="number" min="1" class="form-control form-control-sm mr-1 mb-2" style="width: 70px;" title="확인 주기(시간)" />
          <span class="small mr-2 mb-2">시간마다</span>
          <input v-model="sheet.emailHeader" class="form-control form-control-sm mr-2 mb-2" style="width: 140px;" placeholder="이메일 칸 이름(선택)" />
          <label class="mr-3 mb-2 small"><input v-model="sheet.enabled" type="checkbox" class="mr-1" />자동 지급 켬</label>
          <button class="btn btn-primary btn-sm mr-2 mb-2" :disabled="busy" @click="saveSheet">저장</button>
        </div>
        <div class="d-flex flex-wrap align-items-center mb-2">
          <button class="btn btn-outline-secondary btn-sm mr-2" :disabled="busy || !sheetSaved" @click="syncSheet(true)">새 행 미리보기</button>
          <button class="btn btn-warning btn-sm mr-2" :disabled="busy || !sheetSaved" @click="syncSheet(false)">지금 지급</button>
          <a class="btn btn-outline-success btn-sm" href="/api/admin/coupons/sheet-results.csv" target="_blank">결과 CSV</a>
          <span v-if="sheetSaved" class="small text-muted ml-3">저장됨 {{ fmt(sheetSaved.updatedAt) }} · {{ sheetSaved.updatedBy }} · {{ sheetSaved.enabled ? '자동 켜짐 · ' + (sheetSaved.intervalHours || 4) + '시간마다' : '자동 꺼짐' }}<span v-if="sheetSaved.lastRunAt"> · 마지막 확인 {{ fmt(sheetSaved.lastRunAt) }}</span></span>
        </div>
        <div v-if="sheetPreview" class="small mb-2">새 행 {{ sheetPreview.fresh || 0 }} / 전체 {{ sheetPreview.total || 0 }}{{ sheetPreview.skipped ? ' · ' + sheetPreview.skipped : '' }}{{ sheetPreviewDry ? ' (미리보기 — 아직 지급 안 함)' : '' }}</div>
        <div class="table-wrap">
          <table v-if="sheetTable.length" class="table table-sm table-bordered mb-0">
            <thead><tr><th>행</th><th>이메일</th><th>가입</th><th>결과</th><th>까닭</th></tr></thead>
            <tbody>
              <tr v-for="r in sheetTable" :key="r.emailKey + '-' + r.row">
                <td>{{ r.row }}</td>
                <td>{{ r.email }}</td>
                <td>{{ r.registered ? '가입' : '미가입' }}</td>
                <td><span class="badge" :class="resultClass(r.result)">{{ resultLabel(r) }}</span></td>
                <td class="small">{{ reasonLabel(r.reason) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminCoupons',
  data() {
    return {
      busy: false,
      message: '',
      messageOk: true,
      reasons: {},
      issueForm: { emails: '', batch: '', days: 30 },
      issueResults: [],
      transferForm: { from: '', to: '', reason: '' },
      listForm: { q: '', batch: '' },
      coupons: [],
      logs: [],
      sheet: { url: '', batch: '', days: 30, intervalHours: 4, emailHeader: '', enabled: false },
      sheetSaved: null,
      sheetRows: [],
      sheetPreview: null,
      sheetPreviewDry: false,
    };
  },
  computed: {
    sheetTable() {
      return this.sheetPreview && this.sheetPreview.results && this.sheetPreview.results.length ? this.sheetPreview.results : this.sheetRows;
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    say(text, ok) { this.message = text; this.messageOk = ok !== false; },
    fmt(t) {
      if (!t) return '';
      const d = new Date(t);
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    reasonLabel(r) { return r ? (this.reasons[r] || r) : ''; },
    resultLabel(r) {
      if (r.result === 'issued') return r.registered ? '발급' : '미리 발급(미가입)';
      if (r.result === 'would-issue') return r.registered ? '발급 예정' : '미리 발급 예정(미가입)';
      return '건너뜀';
    },
    resultClass(res) { return res === 'issued' ? 'badge-success' : res === 'would-issue' ? 'badge-info' : 'badge-secondary'; },
    actionLabel(a) {
      return ({ issue: '발급', 'issue-skip': '건너뜀', transfer: '이전', 'transfer-undo': '이전 되돌림', revoke: '회수', 'revoke-undo': '회수 취소', 'sheet-config': '시트 설정' })[a] || a;
    },
    logText(l) {
      if (l.action === 'issue') return `${l.email} · ${l.batch} · ${l.days}일${l.note === 'pending-signup' ? ' · 미가입 미리' : ''}${l.source === 'sheet' ? ' · 시트' : ''}`;
      if (l.action === 'issue-skip') return (l.skipped || []).map((s) => `${s.email}(${this.reasonLabel(s.reason)})`).join(', ');
      if (l.action === 'transfer' || l.action === 'transfer-undo') return `${l.before && l.before.email} → ${l.after && l.after.email} · ${l.batch || ''}${l.reason ? ' · ' + l.reason : ''}`;
      if (l.action === 'revoke' || l.action === 'revoke-undo') return `${l.email || ''} · ${l.batch || ''}${l.reason ? ' · ' + l.reason : ''}`;
      if (l.action === 'sheet-config') return `${l.after && l.after.batch} · ${l.after && l.after.enabled ? '켬' : '끔'}`;
      return '';
    },
    async post(url, body) {
      const r = await axios.post(url, body || {});
      if (r.data && r.data.reasons) this.reasons = r.data.reasons;
      return r.data || {};
    },
    async loadAll() {
      await Promise.all([this.loadList(), this.loadSheet()]);
    },
    async loadList() {
      try {
        const d = await this.post('/api/admin/coupons/list', { q: this.listForm.q, batch: this.listForm.batch });
        if (!d.success) return this.say(d.message || '목록을 불러오지 못했습니다.', false);
        this.coupons = d.data.coupons;
        this.logs = d.data.logs;
      } catch (e) {
        console.error('쿠폰 목록 실패', e);
        this.say('목록을 불러오지 못했습니다.', false);
      }
    },
    async issue(dryRun) {
      if (!this.issueForm.batch.trim()) return this.say('batch 를 넣어 주세요.', false);
      if (!dryRun && !window.confirm('발급할까요? (되돌리기는 기록에서)')) return;
      this.busy = true;
      try {
        const d = await this.post('/api/admin/coupons/issue', Object.assign({ dryRun }, this.issueForm));
        if (!d.success) return this.say(d.message || '발급 실패', false);
        this.issueResults = d.data.results;
        const n = d.data.results.filter((r) => r.result === (dryRun ? 'would-issue' : 'issued')).length;
        this.say(`${dryRun ? '미리보기' : '발급'} — ${n}건${dryRun ? ' 발급 예정' : ' 발급'}, ${d.data.results.length - n}건 건너뜀`);
        if (!dryRun) await this.loadList();
      } catch (e) {
        console.error('쿠폰 발급 실패', e);
        this.say('발급 요청이 실패했습니다.', false);
      } finally { this.busy = false; }
    },
    async transfer() {
      if (!window.confirm(`${this.transferForm.from} 의 안 쓴 쿠폰을 ${this.transferForm.to} 로 옮길까요?`)) return;
      this.busy = true;
      try {
        const d = await this.post('/api/admin/coupons/transfer', this.transferForm);
        if (!d.success) return this.say(d.message || '이전 실패', false);
        this.say(`이전 완료 — ${d.data.moved.length}장 → ${d.data.to}${d.data.toRegistered ? '' : ' (아직 미가입 — 가입하면 사용 가능)'}`);
        await this.loadList();
      } catch (e) {
        console.error('쿠폰 이전 실패', e);
        this.say('이전 요청이 실패했습니다.', false);
      } finally { this.busy = false; }
    },
    async revoke(c) {
      if (!window.confirm(`${c.email} 의 ${c.batch} 쿠폰을 회수할까요?`)) return;
      this.busy = true;
      try {
        const d = await this.post('/api/admin/coupons/revoke', { couponId: c._id });
        if (!d.success) return this.say(d.message || '회수 실패', false);
        this.say('회수했습니다.');
        await this.loadList();
      } catch (e) {
        console.error('쿠폰 회수 실패', e);
        this.say('회수 요청이 실패했습니다.', false);
      } finally { this.busy = false; }
    },
    async undo(l) {
      if (!window.confirm(`«${this.actionLabel(l.action)}» 기록을 되돌릴까요?`)) return;
      this.busy = true;
      try {
        const d = await this.post('/api/admin/coupons/undo', { logId: l._id });
        if (!d.success) return this.say(d.message || '되돌리기 실패', false);
        this.say('되돌렸습니다.');
        await this.loadList();
      } catch (e) {
        console.error('쿠폰 되돌리기 실패', e);
        this.say('되돌리기 요청이 실패했습니다.', false);
      } finally { this.busy = false; }
    },
    applySheet(data) {
      const c = data && data.config;
      this.sheetSaved = c || null;
      if (c) this.sheet = { url: c.url, batch: c.batch, days: c.days, intervalHours: c.intervalHours || 4, emailHeader: c.emailHeader || '', enabled: !!c.enabled };
      this.sheetRows = (data && data.rows) || [];
    },
    async loadSheet() {
      try {
        const d = await this.post('/api/admin/coupons/sheet-config', {});
        if (d.success) this.applySheet(d.data);
      } catch (e) {
        console.error('시트 설정 조회 실패', e);
      }
    },
    async saveSheet() {
      this.busy = true;
      try {
        const d = await this.post('/api/admin/coupons/sheet-config', Object.assign({ save: true }, this.sheet));
        if (!d.success) return this.say(d.message || '저장 실패', false);
        this.applySheet(d.data);
        this.say('시트 설정을 저장했습니다.');
        await this.loadList();
      } catch (e) {
        console.error('시트 설정 저장 실패', e);
        this.say('저장 요청이 실패했습니다.', false);
      } finally { this.busy = false; }
    },
    async syncSheet(dryRun) {
      if (!dryRun && !window.confirm('시트 새 행에 지금 지급할까요?')) return;
      this.busy = true;
      try {
        const d = await this.post('/api/admin/coupons/sheet-sync', { dryRun });
        if (!d.success) return this.say(d.message || '시트 확인 실패', false);
        this.sheetPreview = d.data;
        this.sheetPreviewDry = dryRun;
        this.say(`시트 확인 — 새 행 ${d.data.fresh || 0}${d.data.skipped ? ' · ' + d.data.skipped : ''}`);
        if (!dryRun) { await this.loadSheet(); await this.loadList(); }
      } catch (e) {
        console.error('시트 확인 실패', e);
        this.say('시트 확인 요청이 실패했습니다.', false);
      } finally { this.busy = false; }
    },
  },
};
</script>

<style scoped>
.admin-coupons .card { margin-bottom: 1rem; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08); }
.admin-coupons .table-wrap { max-height: 420px; overflow: auto; }
</style>
